import { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { errText } from '@/lib/errText';
import { Users, Loader2, Search, ArrowRight, Shield, ShoppingBag, ShieldCheck, Eye, Link2, Undo2 } from 'lucide-react';

// AUTH-06: visual mapping for split-auth staff_role. `full_admin` is the highest
// privilege (green shield), `authenticator` is review-only (softer teal eye).
const STAFF_ROLE_BADGES = {
  full_admin: { label: 'Full Admin', Icon: Shield, bg: 'rgba(14,107,93,0.10)', color: '#0E6B5D', border: 'rgba(14,107,93,0.22)' },
  authenticator: { label: 'Authenticator', Icon: Eye, bg: 'rgba(59,130,246,0.10)', color: '#1D4ED8', border: 'rgba(59,130,246,0.22)' },
};

export default function UsersPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState(params.get('search') || '');
  const [roleFilter, setRoleFilter] = useState(params.get('role') || '');
  const [staffRoleFilter, setStaffRoleFilter] = useState(params.get('staff_role') || '');
  const [sortBy, setSortBy] = useState(params.get('sort') || 'newest');
  const page = parseInt(params.get('page') || '1');
  const [mergeCtx, setMergeCtx] = useState(null);
  // Aug 2026: Grouping (non-destructive replacement for merge). openGroup opens
  // a picker to link accounts under a primary; nothing is moved, deleted, or renamed.
  const [groupCtx, setGroupCtx] = useState(null); // { user, memberInput, saving }
  // Aug 2026: Unmerge preview + action for any user whose merged_into is set.
  const [unmergeCtx, setUnmergeCtx] = useState(null); // { user, preview, loading, running, mode }

  const openGroup = (user) => setGroupCtx({ user, memberInput: '', saving: false, memberEmails: [] });
  const addGroupMember = () => {
    const email = (groupCtx.memberInput || '').trim().toLowerCase();
    if (!email) return;
    if (groupCtx.memberEmails.includes(email)) { toast.error('Already added'); return; }
    setGroupCtx(g => ({ ...g, memberEmails: [...g.memberEmails, email], memberInput: '' }));
  };
  const doGroup = async () => {
    if (!groupCtx?.memberEmails?.length) { toast.error('Add at least one member email'); return; }
    setGroupCtx(g => ({ ...g, saving: true }));
    try {
      // Aug 2026 batch: ONE atomic POST with the whole email list.
      // The backend validates every email before writing, so a bad
      // entry mid-list can't leave the group half-linked.
      const { data } = await api.post(
        `/admin/users/${groupCtx.user.id}/group`,
        { emails: groupCtx.memberEmails },
      );
      const linked = data.linked ?? groupCtx.memberEmails.length;
      toast.success(`Grouped — linked ${linked} account(s) to ${groupCtx.user.email}`);
      setGroupCtx(null);
      await load();
    } catch (e) {
      toast.error(errText(e, 'Group failed'));
      setGroupCtx(g => ({ ...g, saving: false }));
    }
  };

  const openUnmerge = async (user) => {
    setUnmergeCtx({ user, preview: null, loading: true, running: false, mode: 'ids' });
    try {
      const { data } = await api.get(`/admin/users/${user.id}/unmerge/preview`);
      // Suggest 'ids' when the audit recorded specific IDs (precise reverse).
      // Otherwise fall back to 'all' — the preview dialog surfaces the
      // safe_all_reverse warning when the keeper looks like it had its own
      // data, so the admin can decide whether to proceed.
      const suggestedMode = data.has_moved_ids ? 'ids' : 'all';
      setUnmergeCtx(c => ({ ...c, preview: data, loading: false, mode: suggestedMode }));
    } catch (e) {
      toast.error(errText(e, 'Preview failed'));
      setUnmergeCtx(null);
    }
  };
  const doUnmerge = async () => {
    if (!unmergeCtx?.preview) return;
    setUnmergeCtx(c => ({ ...c, running: true }));
    try {
      const { data } = await api.post(`/admin/users/${unmergeCtx.user.id}/unmerge`, { mode: unmergeCtx.mode });
      const total = Object.values(data.restored || {}).reduce((s, n) => s + n, 0);
      toast.success(`Unmerged — restored ${total} records · email → ${data.email}`);
      setUnmergeCtx(null);
      await load();
    } catch (e) {
      toast.error(errText(e, 'Unmerge failed'));
      setUnmergeCtx(c => ({ ...c, running: false }));
    }
  };


  const openMerge = (user) => setMergeCtx({ user, mergeInto: user.duplicate_hint?.[0]?.user_id || '', merging: false, preview: null, previewLoading: false, previewError: null });
  const loadPreview = async () => {
    if (!mergeCtx?.mergeInto) { toast.error('Pick a keeper account first'); return; }
    setMergeCtx(m => ({ ...m, previewLoading: true, previewError: null, preview: null }));
    try {
      const { data } = await api.get(`/admin/users/${mergeCtx.mergeInto}/merge/preview`, {
        params: { source_user_id: mergeCtx.user.id },
      });
      setMergeCtx(m => ({ ...m, preview: data, previewLoading: false }));
    } catch (e) {
      setMergeCtx(m => ({ ...m, previewError: errText(e, 'Preview failed'), previewLoading: false }));
    }
  };
  const doMerge = async () => {
    if (!mergeCtx?.mergeInto) { toast.error('Pick a keeper account first'); return; }
    setMergeCtx(m => ({ ...m, merging: true }));
    try {
      const { data } = await api.post(`/admin/users/${mergeCtx.mergeInto}/merge`, { source_user_id: mergeCtx.user.id });
      const total = Object.values(data.moved || {}).reduce((s, n) => s + n, 0);
      toast.success(`Merged — moved ${total} records to keeper account`);
      setMergeCtx(null);
      await load();
    } catch (e) {
      toast.error(errText(e, 'Merge failed'));
      setMergeCtx(m => ({ ...m, merging: false }));
    }
  };

  // Debounced search input (Aug 2026 fix): typing fires one request
  // 300ms after the user stops, not on every keystroke. Also: when
  // every filter is empty, we show NO results instead of defaulting
  // to all 700+ users.
  const [debouncedSearch, setDebouncedSearch] = useState(searchText);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const load = useCallback(async () => {
    // Guard: no filter at all → no fetch, no results. Prevents the
    // "typed into the box, cleared it, now flooded with 700 users"
    // regression and the initial-load 700-row flash.
    const noFilters = !debouncedSearch.trim() && !roleFilter && !staffRoleFilter;
    if (noFilters) {
      setData({ users: [], total: 0, page: 1, limit: 50 });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const qp = new URLSearchParams({ page: String(page), limit: '50' });
      if (debouncedSearch) qp.set('search', debouncedSearch);
      if (roleFilter) qp.set('role', roleFilter);
      if (staffRoleFilter) qp.set('staff_role', staffRoleFilter);
      if (sortBy) qp.set('sort', sortBy);
      const r = await api.get(`/admin/users?${qp}`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, [page, debouncedSearch, roleFilter, staffRoleFilter, sortBy]);

  useEffect(() => { load(); }, [load]);

  // Write the current search + role into the URL as a FRESH param set, which
  // drops any stale `page` — i.e. always resets pagination to page 1. Used by
  // both the Search button and the role filters so a filter change can't strand
  // the user on a now-out-of-range page (USR-01).
  const applyFilters = (nextRole = roleFilter, nextSearch = searchText, nextStaffRole = staffRoleFilter, nextSort = sortBy) => {
    const newP = new URLSearchParams();
    if (nextSearch) newP.set('search', nextSearch);
    if (nextRole) newP.set('role', nextRole);
    if (nextStaffRole) newP.set('staff_role', nextStaffRole);
    if (nextSort && nextSort !== 'newest') newP.set('sort', nextSort);
    setParams(newP);
  };

  const doSearch = () => applyFilters();

  return (
    <div data-testid="users-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-ink" />
          <h1 className="text-base font-semibold text-ink">Users</h1>
          <Badge variant="outline" className="text-[10px]">{data?.total || 0} total</Badge>
        </div>
      </div>

      {/* Search & filter toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doSearch()}
            placeholder="Search by email, name, or coupon code..."
            className="pl-8 h-8 text-xs"
            data-testid="user-search-input"
          />
        </div>
        <Select value={roleFilter || 'all'} onValueChange={v => { const next = v === 'all' ? '' : v; setRoleFilter(next); applyFilters(next, searchText, staffRoleFilter); }}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="user-role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="customer">Customer</SelectItem>
          </SelectContent>
        </Select>
        <Select value={staffRoleFilter || 'all'} onValueChange={v => { const next = v === 'all' ? '' : v; setStaffRoleFilter(next); applyFilters(roleFilter, searchText, next); }}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="user-staff-role-filter"><SelectValue placeholder="All Staff Privileges" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Staff Privileges</SelectItem>
            <SelectItem value="full_admin">Full Admin</SelectItem>
            <SelectItem value="authenticator">Authenticator</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={v => { setSortBy(v); applyFilters(roleFilter, searchText, staffRoleFilter, v); }}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="user-sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest account</SelectItem>
            <SelectItem value="oldest">Oldest account</SelectItem>
            <SelectItem value="name_asc">Name (A–Z)</SelectItem>
            <SelectItem value="orders_desc">Most orders</SelectItem>
            <SelectItem value="orders_asc">Fewest orders</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={doSearch}>
          <Search className="h-3 w-3 mr-1" /> Search
        </Button>
      </div>

      {/* User list */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : (data?.users || []).length === 0 ? (
        <Card className="bg-white border-stone/50">
          <CardContent className="p-8 text-center">
            <Users className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No users found{searchText ? ` matching "${searchText}"` : ''}.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {data.users.map(u => {
            const staffBadge = u.staff_role ? STAFF_ROLE_BADGES[u.staff_role] : null;
            return (
              <Link key={u.id} to={`/team/users/${u.id}`} className="block no-underline" data-testid={`user-row-${u.id}`}>
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-stone/30 bg-white hover:shadow-sm hover:border-stone/50 transition-all duration-150">
                  {/* Avatar circle */}
                  <div className="h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0" style={{
                    background: u.roles?.includes('admin') ? 'rgba(14,107,93,0.10)' : 'hsl(var(--bg-paper-2))'
                  }}>
                    <span className="text-xs font-semibold" style={{ color: u.roles?.includes('admin') ? '#0E6B5D' : 'hsl(var(--muted-ink))' }}>
                      {(u.display_name || u.email || '?')[0].toUpperCase()}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-ink truncate">{u.display_name || u.email.split('@')[0]}</span>
                      {staffBadge ? (
                        <Badge
                          className="text-[9px] h-4"
                          style={{ background: staffBadge.bg, color: staffBadge.color, border: `1px solid ${staffBadge.border}` }}
                          data-testid={`user-staff-role-badge-${u.staff_role}`}
                        >
                          <staffBadge.Icon className="h-2.5 w-2.5 mr-0.5" /> {staffBadge.label}
                        </Badge>
                      ) : u.roles?.includes('admin') && (
                        // Fallback: user has legacy admin role but no split-auth staff_accounts row yet.
                        <Badge className="text-[9px] h-4" style={{ background: 'rgba(14,107,93,0.10)', color: '#0E6B5D', border: '1px solid rgba(14,107,93,0.22)' }}>
                          <ShieldCheck className="h-2.5 w-2.5 mr-0.5" /> Admin
                        </Badge>
                      )}
                      {(u.duplicate_hint || []).length > 0 && !u.merged_into && (
                        <>
                          <Badge
                            className="text-[9px] h-4"
                            style={{ background: 'rgba(180,120,20,0.10)', color: '#8A562B', border: '1px solid rgba(180,120,20,0.28)' }}
                            title={u.duplicate_hint.map(h => `${h.reason}: ${h.match}`).join(' · ')}
                            data-testid={`user-duplicate-hint-${u.id}`}
                          >
                            Possible duplicate ({u.duplicate_hint.length})
                          </Badge>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); openGroup(u); }}
                            className="text-[9px] px-1.5 py-0.5 rounded border border-stone/40 text-[#0E6B5D] hover:bg-stone/10 flex items-center gap-0.5"
                            data-testid={`group-btn-${u.id}`}
                            title="Group accounts (non-destructive)"
                          >
                            <Link2 className="h-2.5 w-2.5" /> Group…
                          </button>
                        </>
                      )}
                      {(u.account_group_id || u.group_id) && (
                        <>
                          <Badge
                            className="text-[9px] h-4"
                            style={{ background: 'rgba(14,107,93,0.10)', color: '#0E6B5D', border: '1px solid rgba(14,107,93,0.22)' }}
                            data-testid={`user-grouped-badge-${u.id}`}
                            title="This account is grouped with one or more others"
                          >
                            <Link2 className="h-2.5 w-2.5 mr-0.5" /> Grouped
                          </Badge>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              if (!window.confirm(`Ungroup ${u.email}?`)) return;
                              try {
                                await api.post(`/admin/users/${u.id}/ungroup`);
                                toast.success('Ungrouped');
                                await load();
                              } catch (err) {
                                toast.error(errText(err, 'Ungroup failed'));
                              }
                            }}
                            className="text-[9px] px-1.5 py-0.5 rounded border border-stone/40 text-muted-foreground hover:text-ink hover:bg-stone/10"
                            data-testid={`ungroup-btn-${u.id}`}
                          >
                            Ungroup
                          </button>
                        </>
                      )}
                      {u.merged_into && (
                        <>
                          <Badge
                            className="text-[9px] h-4"
                            style={{ background: 'rgba(120,50,50,0.10)', color: '#5A1F2A', border: '1px solid rgba(120,50,50,0.22)' }}
                            data-testid={`user-merged-badge-${u.id}`}
                            title="This account was merged into another. Click Unmerge to restore."
                          >
                            Merged (deprecated)
                          </Badge>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); openUnmerge(u); }}
                            className="text-[9px] px-1.5 py-0.5 rounded border border-stone/40 text-[#5A1F2A] hover:bg-stone/10 flex items-center gap-0.5"
                            data-testid={`unmerge-btn-${u.id}`}
                          >
                            <Undo2 className="h-2.5 w-2.5" /> Unmerge
                          </button>
                        </>
                      )}
                    </div>
                    <span className="text-[11px] text-muted-foreground">{u.email}</span>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 flex-shrink-0 hidden sm:flex">
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <ShoppingBag className="h-3 w-3" />
                        {u.order_count} order{u.order_count !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                    </span>
                  </div>

                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data && data.total > 50 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" className="text-xs" disabled={page <= 1}
            onClick={() => { const p = new URLSearchParams(params); p.set('page', String(page-1)); setParams(p); }}>Previous</Button>
          <span className="text-xs text-muted-foreground">Page {page}</span>
          <Button variant="outline" size="sm" className="text-xs" disabled={data.users.length < 50}
            onClick={() => { const p = new URLSearchParams(params); p.set('page', String(page+1)); setParams(p); }}>Next</Button>
        </div>
      )}

      {/* Group dialog (Aug 2026) — non-destructive account linking. */}
      <Dialog open={!!groupCtx} onOpenChange={(v) => !v && setGroupCtx(null)}>
        <DialogContent className="max-w-lg" data-testid="group-dialog">
          <DialogHeader><DialogTitle className="text-sm">Group accounts</DialogTitle></DialogHeader>
          {groupCtx && (
            <div className="space-y-3 text-xs">
              <p className="text-muted-foreground">
                Link one or more accounts to <span className="font-mono text-ink">{groupCtx.user.email}</span> as
                the primary. <strong>Nothing is moved or deleted</strong> — each account keeps its own login, orders,
                addresses, and email. Team pages can view the group as a single customer.
              </p>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Add member email</label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={groupCtx.memberInput}
                    onChange={(e) => setGroupCtx({ ...groupCtx, memberInput: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGroupMember(); } }}
                    placeholder="petuniasresale@gmail.com"
                    className="h-8 text-xs" data-testid="group-member-input"
                  />
                  <Button type="button" size="sm" variant="outline" className="text-xs h-8"
                          onClick={addGroupMember} data-testid="group-member-add-btn">Add</Button>
                </div>
              </div>
              {groupCtx.memberEmails.length > 0 && (
                <div className="border border-stone/40 rounded p-2 space-y-1" data-testid="group-member-list">
                  {groupCtx.memberEmails.map(em => (
                    <div key={em} className="flex items-center justify-between">
                      <span className="font-mono text-ink">{em}</span>
                      <button type="button" className="text-[10px] text-muted-foreground hover:text-[#5A1F2A]"
                              onClick={() => setGroupCtx(g => ({ ...g, memberEmails: g.memberEmails.filter(x => x !== em) }))}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">
                Reversible: click <strong>Ungroup</strong> on any member later to unlink.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setGroupCtx(null)} disabled={groupCtx?.saving}>Cancel</Button>
            <Button size="sm" className="text-xs" onClick={doGroup}
                    disabled={groupCtx?.saving || !groupCtx?.memberEmails?.length}
                    data-testid="group-confirm-btn">
              {groupCtx?.saving ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Grouping…</> : 'Create group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unmerge dialog (Aug 2026) — recovery tool for prior merges. */}
      <Dialog open={!!unmergeCtx} onOpenChange={(v) => !v && setUnmergeCtx(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="unmerge-dialog">
          <DialogHeader><DialogTitle className="text-sm">Unmerge account</DialogTitle></DialogHeader>
          {unmergeCtx && (
            unmergeCtx.loading ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading preview…
              </div>
            ) : unmergeCtx.preview && (
              <div className="space-y-3 text-xs">
                <p className="text-muted-foreground">
                  Restore <span className="font-mono text-ink">{unmergeCtx.preview.source.current_email}</span> to{' '}
                  <span className="font-mono text-ink">{unmergeCtx.preview.source.original_email || '(original email unknown)'}</span> and
                  move records back from the keeper.
                </p>
                <div className="border border-stone/40 rounded p-3 space-y-2 bg-stone/10">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Keeper</div>
                  <div className="font-mono text-ink" data-testid="unmerge-keeper-email">{unmergeCtx.preview.keeper.email || unmergeCtx.preview.keeper.id}</div>
                  <div className="grid grid-cols-2 gap-1 text-[11px]">
                    {Object.entries(unmergeCtx.preview.keeper_current).map(([k, v]) => (
                      <div key={k}>
                        <span className="text-muted-foreground">{k}:</span>{' '}
                        <span className="font-medium">{v}</span>
                        {unmergeCtx.preview.audit_moved?.[k] != null && (
                          <span className="text-muted-foreground"> (merge moved {unmergeCtx.preview.audit_moved[k]})</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Restore mode</label>
                  {unmergeCtx.preview.has_moved_ids ? (
                    <p className="text-[11px] text-ink mt-1">
                      <strong>Precise reverse</strong> — only the exact records the merge touched will move back
                      (recorded in the audit trail).
                    </p>
                  ) : unmergeCtx.preview.safe_all_reverse ? (
                    <p className="text-[11px] text-ink mt-1">
                      <strong>Bulk reverse (safe)</strong> — the keeper only holds records that came from the source,
                      so moving <em>everything</em> back is safe.
                    </p>
                  ) : (
                    <div className="mt-1 p-2 border border-amber-300 bg-amber-50 rounded text-[11px]">
                      <p className="font-semibold text-amber-900">Bulk reverse (caution)</p>
                      <p className="text-amber-900 mt-1">
                        The audit didn&apos;t record record IDs and the keeper appears to hold its own data too. Bulk
                        unmerge will move <strong>ALL</strong> the keeper&apos;s orders/addresses/notes to the source.
                        Only proceed if the keeper account was a junk/duplicate signup.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setUnmergeCtx(null)} disabled={unmergeCtx?.running}>Cancel</Button>
            <Button size="sm" className="text-xs" onClick={doUnmerge}
                    disabled={unmergeCtx?.running || unmergeCtx?.loading || !unmergeCtx?.preview}
                    data-testid="unmerge-confirm-btn">
              {unmergeCtx?.running ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Restoring…</> : 'Unmerge & restore'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge duplicates modal (Feb 2026, preview added Aug 2026) */}
      <Dialog open={!!mergeCtx} onOpenChange={(v) => !v && setMergeCtx(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="merge-dialog">
          <DialogHeader><DialogTitle className="text-sm">Merge duplicate account</DialogTitle></DialogHeader>
          {mergeCtx && (
            <div className="space-y-3 text-xs">
              <p className="text-muted-foreground">
                All orders, addresses, notes, and profile data from{' '}
                <span className="font-mono text-ink">{mergeCtx.user.email}</span> will be
                reassigned to the keeper account. The source account will be soft-deleted
                and its email renamed so it can be reused.
              </p>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Keeper account</label>
                <select
                  value={mergeCtx.mergeInto}
                  onChange={(e) => setMergeCtx({ ...mergeCtx, mergeInto: e.target.value, preview: null, previewError: null })}
                  className="w-full mt-1 p-2 rounded border border-stone text-xs"
                  data-testid="merge-keeper-select"
                >
                  <option value="">Select keeper…</option>
                  {(mergeCtx.user.duplicate_hint || []).map(h => (
                    <option key={h.user_id} value={h.user_id}>
                      {(h.reason === 'name' ? `Name match: ${h.match}` : `Address match: ${h.match}`) + ` (${h.user_id.slice(0,8)}…)`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Preview panel */}
              <div className="border border-stone rounded p-3 bg-stone/20 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Before you merge</span>
                  <Button
                    variant="outline" size="sm" className="text-[11px] h-7"
                    onClick={loadPreview}
                    disabled={!mergeCtx.mergeInto || mergeCtx.previewLoading}
                    data-testid="merge-load-preview-btn"
                  >
                    {mergeCtx.previewLoading ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Loading…</> : (mergeCtx.preview ? 'Refresh preview' : 'Load preview')}
                  </Button>
                </div>

                {!mergeCtx.preview && !mergeCtx.previewLoading && !mergeCtx.previewError && (
                  <p className="text-[11px] text-muted-foreground">Click &quot;Load preview&quot; to see exactly which records will move.</p>
                )}

                {mergeCtx.previewError && (
                  <p className="text-[11px] text-[#5A1F2A]" data-testid="merge-preview-error">
                    {mergeCtx.previewError}
                  </p>
                )}

                {mergeCtx.preview && (
                  <div className="space-y-2" data-testid="merge-preview-panel">
                    <div className="grid grid-cols-2 gap-1 text-[11px]">
                      <div><span className="text-muted-foreground">Orders:</span> <span className="font-medium">{mergeCtx.preview.orders.count}</span></div>
                      <div><span className="text-muted-foreground">Addresses:</span> <span className="font-medium">{mergeCtx.preview.addresses.count}</span></div>
                      <div><span className="text-muted-foreground">Notes:</span> <span className="font-medium">{mergeCtx.preview.staff_notes.count}</span></div>
                      <div><span className="text-muted-foreground">Emails:</span> <span className="font-medium">{mergeCtx.preview.email_deliveries.count} · events {mergeCtx.preview.email_events.count}</span></div>
                      <div className="col-span-2">
                        <span className="text-muted-foreground">Profile:</span>{' '}
                        <span className="font-medium">
                          {mergeCtx.preview.customer_profile.action === 'drop_source' && 'both have profiles → source dropped'}
                          {mergeCtx.preview.customer_profile.action === 'transfer' && 'source profile transferred to keeper'}
                          {mergeCtx.preview.customer_profile.action === 'none' && 'no profile on source'}
                        </span>
                      </div>
                    </div>

                    {mergeCtx.preview.orders.sample.length > 0 && (
                      <details className="text-[11px]" data-testid="merge-preview-orders">
                        <summary className="cursor-pointer text-muted-foreground">Orders sample (up to {mergeCtx.preview.sample_limit})</summary>
                        <ul className="mt-1 space-y-1 pl-3">
                          {mergeCtx.preview.orders.sample.map(o => (
                            <li key={o.id}>
                              <span className="font-mono">{o.order_number || o.id?.slice(0,8)}</span>
                              {' · '}${(o.total ?? 0).toFixed(2)} · {o.status}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    {mergeCtx.preview.addresses.sample.length > 0 && (
                      <details className="text-[11px]" data-testid="merge-preview-addresses">
                        <summary className="cursor-pointer text-muted-foreground">Addresses sample (up to {mergeCtx.preview.sample_limit})</summary>
                        <ul className="mt-1 space-y-1 pl-3">
                          {mergeCtx.preview.addresses.sample.map(a => (
                            <li key={a.id}>
                              {[a.city, a.state, a.postal_code, a.country].filter(Boolean).join(', ')}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    {mergeCtx.preview.staff_notes.sample.length > 0 && (
                      <details className="text-[11px]" data-testid="merge-preview-notes">
                        <summary className="cursor-pointer text-muted-foreground">Notes sample (up to {mergeCtx.preview.sample_limit})</summary>
                        <ul className="mt-1 space-y-1 pl-3">
                          {mergeCtx.preview.staff_notes.sample.map(n => (
                            <li key={n.id} className="truncate">
                              {n.author_email && <span className="text-muted-foreground">{n.author_email}: </span>}
                              {(n.note || '').slice(0, 80)}{(n.note || '').length > 80 ? '…' : ''}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>

              <p className="text-[10px] text-[#5A1F2A]">
                This action is <strong>not reversible</strong> from the UI. Only proceed if you&#39;re sure the two accounts belong to the same person.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" className="text-xs" onClick={() => setMergeCtx(null)} disabled={mergeCtx?.merging}>Cancel</Button>
            <Button size="sm" className="text-xs" onClick={doMerge} disabled={!mergeCtx?.mergeInto || mergeCtx?.merging || !mergeCtx?.preview} data-testid="merge-confirm-btn">
              {mergeCtx?.merging ? <><Loader2 className="h-3 w-3 animate-spin mr-1" />Merging…</> : 'Merge accounts'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
